import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, accountApi } from '../api/client';
import { Field, LanguageSwitcher, Notice, PasswordInput } from '../components/ui';
import { useI18n } from '../i18n';

/**
 * Öffentliche Registrierung eines neuen Kunden.
 * E-Mail, Passwort, Passwortwiederholung - mehr wird nicht abgefragt.
 */
export function RegisterPage(): JSX.Element {
  const { t, tError } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [customerNumber, setCustomerNumber] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await accountApi.register(email, password, passwordConfirmation);
      setCustomerNumber(result.customerNumber);
    } catch (caught) {
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  if (customerNumber) {
    return (
      <main className="page page-narrow">
        <header className="page-header">
          <h1>{t('register.successTitle')}</h1>
          <LanguageSwitcher />
        </header>
        <Notice tone="success">{t('register.successText')}</Notice>
        <p className="lead">
          {t('register.customerNumber')}: <strong>{customerNumber}</strong>
        </p>
        <p className="field-hint">{t('register.customerNumberHint')}</p>
        <nav className="page-links">
          <Link to="/anmelden">{t('action.login')}</Link>
        </nav>
      </main>
    );
  }

  return (
    <main className="page page-narrow">
      <header className="page-header">
        <h1>{t('register.title')}</h1>
        <LanguageSwitcher />
      </header>

      <form onSubmit={submit} className="form">
        <Field label={t('register.email')}>
          <input
            type="email"
            value={email}
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label={t('register.password')}>
          <PasswordInput value={password} onChange={setPassword} autoComplete="new-password" />
        </Field>

        <Field label={t('register.passwordConfirmation')}>
          <PasswordInput
            value={passwordConfirmation}
            onChange={setPasswordConfirmation}
            autoComplete="new-password"
          />
        </Field>

        {error ? <Notice tone="error">{error}</Notice> : null}

        <button type="submit" className="primary" disabled={busy}>
          {t('register.submit')}
        </button>
      </form>

      <nav className="page-links">
        <Link to="/anmelden">{t('register.haveAccount')}</Link>
      </nav>
    </main>
  );
}
